import * as utilityActions from "../actions/utility-actions";
import { PagedApiResult } from "../models/utilities/paged-request";
import { ToastMessage } from "../models/utilities/toast-message";
import { BaseService } from "../services/_base-service";
import { Api, ApiResponse, StatusCode } from "../utils/api";
import { ApiEndpoints } from "../utils/app-constants";
import { AppUtils } from "../utils/app-utils";
import { Mapper } from "../utils/mapper";
import { MailJobRead } from "../pages/communications/models/mailjobs/mailjob-read";
import { MailJobSearchParams } from "../pages/communications/models/mailjobs/mailjob-search-params";
import { MailJobSummary } from "../pages/communications/models/mailjobs/mailjob-summary";
import { MessageRead } from "../pages/communications/models/messages/message-read";

export class CommunicationSvc extends BaseService {

    /**
     * Trigger the creation of a mailjob
     */
    public forceCreateMailjob = async () : Promise<StatusCode> => {

        let response : StatusCode = StatusCode.Failure;
        this.store.dispatch(utilityActions.showLoading());

        try {

            await Api.Post(
                ApiEndpoints.COMMUNICATION_MAILJOB,
                {}
            );

            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Create Mail Job Request Sent", "success")));
            response = StatusCode.Success;

        }
        catch (err) {

            console.error(err);
            const errorMsg = AppUtils.FormatError(err);
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage(errorMsg, "error")));

        }

        this.store.dispatch(utilityActions.hideLoading());
        return response;
    }

    public printMailJob = async (mailJobId : number) : Promise<StatusCode> => {

        let statusCode = StatusCode.Failure;
        if (AppUtils.IsEmpty(mailJobId)) {
            return statusCode;
        }

        // Update the mailjob
        try {

            this.store.dispatch(utilityActions.showLoading());

            await Api.Put(
                ApiEndpoints.COMMUNICATION_MAILJOB_PRINT.formattify(mailJobId), 
                {}
            );

            statusCode = StatusCode.Success;
        }
        catch (err) {

            console.error(err);
            const errorMsg = AppUtils.FormatError(err);
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage(errorMsg, "error")));

        }

        this.store.dispatch(utilityActions.hideLoading());
        return statusCode;
    }

    /**
     * Get all mail jobs and the mail job summary in one call
     * Used to build the page data for the mail triage page
     */
    public getAllMailjobs = async (openParams : MailJobSearchParams, printedParams : MailJobSearchParams) => {

        this.store.dispatch(utilityActions.showLoading());
        
        const openProm      = this.getMailJobs(openParams);
        const printedProm   = this.getMailJobs(printedParams);
        const summaryProm   = this.getMailJobSummary();

        const result = await Promise.all([openProm, printedProm, summaryProm])
            .then((res : any) => {
                return {
                    open: res[0],
                    printed: res[1],
                    summary: res[2]
                };
            });

        this.store.dispatch(utilityActions.hideLoading());
        return result;
    }

    /**
     * Get the current mailjob summary view model including 
     * pending document count and pending page count
     */
    public getMailJobSummary = async () : Promise<MailJobSummary | undefined> => {

        try {

            const summaryProm = await Api.Get(MailJobSummary, ApiEndpoints.COMMUNICATION_MAILJOB_SUMMARY);
            return summaryProm;

        }
        catch (err) {

            console.error(err);
            const errorMsg = AppUtils.FormatError(err);
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage(errorMsg, "error")));

        }

        return undefined;

    }

    /**
     * Search mailjobs 
     * @param parmas The mail job search parameters to get results for
     */
    public getMailJobs = async (params : MailJobSearchParams) : Promise<PagedApiResult<MailJobRead> | undefined> => {

        try {

            this.store.dispatch(utilityActions.showLoading());
            const result = await Api.PagedGet(MailJobRead, params.toUrl(), params.paginationInfo);
            this.store.dispatch(utilityActions.hideLoading());
            return result;

        }
        catch (err) {

            console.error(err);
            const errorMsg = AppUtils.FormatError(err);
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage(errorMsg, "error")));

        }

        return undefined;
    }

    /**
     * Queue the provided message for resending
     * @param messageId The message ID to resend
     */
    public resendCommunication = async (messageId : number) : Promise<ApiResponse<MessageRead>> => {

        const response = new ApiResponse<MessageRead>();
        this._showLoading();

        try {

            const result = await Api.Post(
                ApiEndpoints.COMMUNICATION_MESSAGE_RESEND.formattify(messageId),
                {}
            );
            
            if (result) {
                response.status = StatusCode.Success;
                response.data = Mapper.deserialize<MessageRead>(MessageRead, result);
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }
}
